import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import {
  Home,
  Key,
  BarChart3,
  Receipt,
  User,
  LogOut,
  Menu,
  X,
  Mic,
  Brain,
  LineChart,
  Users,
  BookOpen,
  MailPlus,
  ShieldOff,
} from "lucide-react";
import { useState } from "react";

const Sidebar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const LANGUAGES = [
    { code: "fr", label: "FR" },
    { code: "de", label: "DE" },
    { code: "it", label: "IT" },
    { code: "en", label: "EN" },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isAdmin = user?.role === "admin";

  const navigation = [
    { name: t("nav.dashboard"), href: "/dashboard", icon: Home },
    { name: t("nav.apiTokens"), href: "/tokens", icon: Key },
    { name: t("nav.usageAnalytics"), href: "/usage", icon: BarChart3 },
    { name: t("nav.billing"), href: "/billing", icon: Receipt },
    { name: t("nav.apiDocs"), href: "/api-docs", icon: BookOpen },
    { name: t("nav.profile"), href: "/profile", icon: User },
    ...(isAdmin
      ? [
          {
            name: t("nav.userManagement"),
            href: "/admin/all-users-billing",
            icon: Users,
          },
          {
            name: t("nav.inviteManagement"),
            href: "/admin/invites",
            icon: MailPlus,
          },
          {
            name: t("nav.tokenManagement"),
            href: "/admin/tokens",
            icon: ShieldOff,
          },
          {
            name: t("nav.transcriptionTesting"),
            href: "/transcription-testing",
            icon: Mic,
          },
          { name: t("nav.llmTesting"), href: "/llm-testing", icon: Brain },
          {
            name: t("nav.werDashboard"),
            href: "/wer-dashboard",
            icon: LineChart,
          },
        ]
      : []),
  ];

  const NavItem = ({ item, mobile = false }) => (
    <NavLink
      to={item.href}
      className={({ isActive }) =>
        `group flex items-center px-3 py-2 text-sm font-medium rounded-2xl transition-colors duration-200 ${
          isActive
            ? "bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 text-white shadow-lg shadow-cyan-700/40"
            : "text-slate-300 hover:text-white hover:bg-slate-800/60"
        } ${mobile ? "text-base" : ""}`
      }
      onClick={() => mobile && setIsMobileMenuOpen(false)}
    >
      <item.icon
        className={`mr-3 flex-shrink-0 h-5 w-5 ${mobile ? "h-6 w-6" : ""}`}
        aria-hidden="true"
      />
      {item.name}
    </NavLink>
  );

  return (
    <div className="relative z-20">
      {/* Mobile menu button */}
      <div className="lg:hidden relative z-30">
        <div className="flex items-center justify-between bg-[#0f172a]/95 px-4 py-3 border-b border-slate-800 text-slate-100 backdrop-blur">
          <div className="flex items-center">
            <div className="h-8 w-8 flex items-center justify-center">
              <img
                src="/logo_epv.png"
                alt="ePV"
                className="h-8 w-8 object-contain scale-145"
              />
            </div>
            <span className="ml-3 text-lg font-semibold tracking-[0.04em] bg-gradient-to-r from-cyan-200 to-indigo-200 bg-clip-text text-transparent">
              ePV
            </span>
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center p-2 rounded-full bg-slate-800/70 text-slate-200 hover:bg-slate-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="bg-[#0b1324] border-b border-slate-800 text-slate-200 shadow-2xl shadow-black/60">
            <div className="px-3 pt-3 pb-4 space-y-1">
              {navigation.map((item) => (
                <NavItem key={item.name} item={item} mobile />
              ))}
              <button
                onClick={handleLogout}
                className="group flex items-center px-3 py-2 text-base font-medium rounded-2xl text-slate-300 hover:text-white hover:bg-slate-800 w-full text-left"
              >
                <LogOut className="mr-3 flex-shrink-0 h-6 w-6" />
                {t("common.signOut")}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="relative flex-1 flex flex-col min-h-0 bg-[#0b1324]/95 border-r border-slate-800 shadow-2xl shadow-black/60">
          <div className="pointer-events-none absolute inset-0 opacity-20">
            <div className="h-full w-full [background:radial-gradient(circle_at_1px,_rgba(226,232,240,0.08)_1px,_transparent_0)] [background-size:30px_30px]" />
          </div>
          <div className="relative flex-1 flex flex-col pt-6 pb-4 overflow-y-auto">
            <div className="flex items-center justify-between flex-shrink-0 px-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 flex items-center justify-center">
                  <img
                    src="/logo_epv.png"
                    alt="ePV"
                    className="h-10 w-10 object-contain scale-145"
                  />
                </div>
                <div>
                  <p className="text-base font-semibold tracking-[0.04em] bg-gradient-to-r from-cyan-200 to-indigo-200 bg-clip-text text-transparent">
                    ePV
                  </p>
                  <p className="text-xs text-slate-400">
                    {t("nav.controlConsole")}
                  </p>
                </div>
              </div>
            </div>
            <nav className="mt-8 flex-1 px-4 space-y-1">
              {navigation.map((item) => (
                <NavItem key={item.name} item={item} />
              ))}
            </nav>
          </div>
          <div className="relative flex-shrink-0 border-t border-slate-800 p-4 bg-[#0b1324]/90 backdrop-blur space-y-3">
            {/* Language switcher */}
            <div className="flex items-center justify-center gap-1">
              {LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  className={`px-2 py-1 rounded-lg text-xs font-semibold transition-colors ${
                    i18n.resolvedLanguage === lang.code
                      ? "bg-gradient-to-r from-cyan-500 to-indigo-500 text-white"
                      : "text-slate-400 hover:text-white hover:bg-slate-800"
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>
            {/* User card */}
            <div className="flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-700 px-4 py-3 bg-slate-900/40">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="h-10 w-10 rounded-full bg-slate-800 flex items-center justify-center flex-shrink-0 text-slate-200">
                  <User className="h-5 w-5" />
                </div>
                <div className="text-sm min-w-0">
                  <p className="font-medium text-white truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-xs text-slate-400 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="inline-flex flex-shrink-0 items-center justify-center rounded-full bg-slate-800 p-2 text-slate-200 hover:text-white"
                title={t("common.signOut")}
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
